import { AfterViewInit, Component, ElementRef, HostListener, OnDestroy, ViewChild } from '@angular/core';
import { NgIf } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Router, RouterModule } from '@angular/router';
import { finalize, switchMap } from 'rxjs/operators';
import { of, Subscription, interval } from 'rxjs';

import { AttachmentService, AuthService, LanguageService } from '../core/services';
import { APP_OTP_EXPIRY_SECONDS, ATTACHMENT_ALLOWED_EXTENSIONS_BY_FEATURE, FeatureTypeEnum } from '../core/constants/app-constants';
import { TranslatePipe } from '../core/pipes/translate.pipe';
import { AuthShowcaseComponent } from '../core/ui/auth-showcase/auth-showcase.component';
import { LanguageSwitchComponent } from '../core/ui/language-switch/language-switch.component';
import {
  combineBangladeshMobileNumber,
  normalizeBangladeshMobileLocalInput,
  BANGLADESH_COUNTRY_CODE
} from '../core/utils/bd-mobile.util';
import { ImageCropModalComponent } from '../core/ui/image-crop-modal/image-crop-modal.component';

type LocalizedCopy = {
  bn: string;
  en: string;
};

type LocalizedListCopy = {
  bn: string[];
  en: string[];
};

type TermsSection = {
  title: LocalizedCopy;
  items: LocalizedListCopy;
};

type IconMarkup = string | SafeHtml;

@Component({
  selector: 'app-sign-up',
  standalone: true,
  imports: [ReactiveFormsModule, RouterModule, NgIf, TranslatePipe, ImageCropModalComponent, LanguageSwitchComponent, AuthShowcaseComponent],
  templateUrl: './sign-up.html',
  styleUrl: './sign-up.css'
})
export class SignUp implements AfterViewInit, OnDestroy {
  private readonly featureGridTransitionMs = 0;
  readonly mobilePrefix = BANGLADESH_COUNTRY_CODE;
  currentStep: 1 | 2 | 3 = 1;
  isOtpStepUnlocked = false;
  focusedField: string | null = null;
  otpValue = '';
  isSendingOtp = false;
  isRegistering = false;
  isImageModalOpen = false;
  isTermsModalOpen = false;
  isFinalizingSignup = false;
  error: string | null = null;
  status: string | null = null;
  selectedImageName = '';
  selectedImageFile: File | null = null;
  selectedImagePreviewUrl: string | null = null;
  imageCropSourceFile: File | null = null;
  registeredUserId: number | null = null;
  otpExpiresInSeconds = 0;
  showPassword = false;
  showConfirmPassword = false;
  private otpCountdownSub: Subscription | null = null;
  private featureVisibilityFrame: number | null = null;
  private featuresResizeObserver: ResizeObserver | null = null;
  private featureGridTransitionTimeout: ReturnType<typeof setTimeout> | null = null;
  private deferredFeatureVisibilityTimeouts: Array<ReturnType<typeof setTimeout>> = [];
  private featureGridColumns = 1;
  private featureGridRowGap = 0;
  private featureGridTileHeight = 72;
  private isFeatureGridTransitioning = false;

  @ViewChild('featuresCard') private featuresCardRef?: ElementRef<HTMLElement>;
  @ViewChild('featuresHeading') private featuresHeadingRef?: ElementRef<HTMLElement>;
  @ViewChild('featureGrid') private featureGridRef?: ElementRef<HTMLElement>;

  readonly showcaseTitle: LocalizedCopy = {
    bn: 'ফ্ল্যাট ম্যানেজমেন্ট সফটওয়্যার',
    en: 'Flat management software'
  };

  readonly showcaseDescription: LocalizedCopy = {
    bn: 'ফ্ল্যাট ম্যানেজমেন্টের সকল কাজকর্ম এক প্ল্যাটফর্মে সহজ, স্বচ্ছ ও নিখুঁতভাবে পরিচালনা করুন।',
    en: 'Manage house, flat operations from one platform with a clear and organized workflow.'
  };

  readonly formSubtitle: LocalizedCopy = {
    bn: 'একটি অ্যাকাউন্ট খুলে আপনার ফ্ল্যাট ম্যানেজমেন্ট ওয়ার্কস্পেস চালু করুন।',
    en: 'Create your account and set up your flat management workspace.'
  };

  readonly featureHeading: LocalizedCopy = {
    bn: 'আমাদের ফ্ল্যাট ম্যানেজমেন্ট সফটওয়্যারটিতে যা যা থাকছে:',
    en: 'Included in our flat management software:'
  };

  isFeaturesExpanded = false;
  renderExpandedFeatures = false;
  collapsedFeatureCount = 6;
  featureGridMaxHeight: number | null = null;

  toggleFeatures() {
    if (this.isFeatureGridTransitioning) {
      return;
    }

    const grid = this.featureGridRef?.nativeElement;
    const currentHeight = Math.ceil(this.featureGridRef?.nativeElement.getBoundingClientRect().height ?? 0);
    if (currentHeight > 0) {
      this.featureGridMaxHeight = currentHeight;
    }

    if (!grid || typeof window === 'undefined') {
      this.renderExpandedFeatures = !this.renderExpandedFeatures;
      this.isFeaturesExpanded = this.renderExpandedFeatures;
      this.scheduleFeatureVisibilityUpdate(true);
      return;
    }

    this.isFeatureGridTransitioning = true;

    if (this.isFeaturesExpanded) {
      const collapsedHeight = this.getCollapsedGridHeight();
      window.requestAnimationFrame(() => {
        this.featureGridMaxHeight = collapsedHeight;
      });

      this.featureGridTransitionTimeout = setTimeout(() => {
        this.featureGridTransitionTimeout = null;
        this.renderExpandedFeatures = false;
        this.isFeaturesExpanded = false;
        this.isFeatureGridTransitioning = false;
        this.scheduleFeatureVisibilityUpdate();
      }, this.featureGridTransitionMs);
      return;
    }

    this.renderExpandedFeatures = true;
    this.isFeaturesExpanded = true;
    window.requestAnimationFrame(() => {
      const expandedHeight = Math.ceil(grid.scrollHeight || grid.getBoundingClientRect().height || 0);
      this.featureGridMaxHeight = expandedHeight > 0 ? expandedHeight : null;
    });

    this.featureGridTransitionTimeout = setTimeout(() => {
      this.featureGridTransitionTimeout = null;
      this.isFeatureGridTransitioning = false;
      this.applyFeatureGridHeight(false);
    }, this.featureGridTransitionMs);
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  toggleConfirmPasswordVisibility(): void {
    this.showConfirmPassword = !this.showConfirmPassword;
  }

  readonly features: Array<{ icon: IconMarkup; title: LocalizedCopy }> = [
    {
      icon: `<svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path fill-rule="evenodd" clip-rule="evenodd" d="M12 2L4 9v11a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9l-8-7zm0 13a3 3 0 1 1 0-6 3 3 0 0 1 0 6z"/></svg>`,
      title: { bn: 'বাড়ি পরিচালনা', en: 'House Management' }
    },
    {
      icon: `<svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path fill-rule="evenodd" clip-rule="evenodd" d="M3 5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5zm12 4H9v2h6V9zm0 4H9v2h6v-2z"/></svg>`,
      title: { bn: 'ফ্ল্যাট পরিচালনা', en: 'Flat Management' }
    },
    {
      icon: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path d="M8 11a3 3 0 1 0 0-6a3 3 0 0 0 0 6Z"/><path d="M16.5 10a2.5 2.5 0 1 0 0-5a2.5 2.5 0 0 0 0 5Z"/><path d="M3.5 18.5c.8-2 2.9-3.3 5.5-3.3s4.7 1.3 5.5 3.3"/><path d="M13 17.8c.7-1.5 2.3-2.5 4.3-2.5c1.7 0 3.2.7 4.2 2"/></svg>`,
      title: { bn: 'ভাড়াটিয়া পরিচালনা', en: 'Renter Management' }
    },
    {
      icon: `<svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path fill-rule="evenodd" clip-rule="evenodd" d="M19 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2zm-7 14h-2v-2h2v2zm3.5-4h-5v-2h5v2zm0-4h-5V7h5v2z"/></svg>`,
      title: { bn: 'ভাড়া ও খরচ ট্র্যাকিং', en: 'Rent & Expense Tracking' }
    },
    {
      icon: `<svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path fill-rule="evenodd" clip-rule="evenodd" d="M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z"/></svg>`,
      title: { bn: 'স্মার্ট ড্যাশবোর্ড', en: 'Smart Dashboard' }
    },
    {
      icon: `<svg width="22" height="20" viewBox="0 0 22 20" fill="currentColor"><path fill-rule="evenodd" clip-rule="evenodd" d="M1 4.54248C1 2.586 2.586 1 4.54248 1H17.4575C19.414 1 21 2.586 21 4.54248V15.4575C21 17.414 19.414 19 17.4575 19H4.54248C2.586 19 1 17.414 1 15.4575V4.54248ZM4.54248 3.51863H17.4575C18.0229 3.51863 18.4814 3.97708 18.4814 4.54248V5.00643H3.51863V4.54248C3.51863 3.97708 3.97708 3.51863 4.54248 3.51863ZM18.4814 7.52494H3.51863V15.4575C3.51863 16.0229 3.97708 16.4814 4.54248 16.4814H17.4575C18.0229 16.4814 18.4814 16.0229 18.4814 15.4575V7.52494ZM6.061 10.0434H11.5367V11.3323H6.061V10.0434ZM15.939 10.0434H13.4722V11.3323H15.939V10.0434ZM6.061 12.6212H15.939V13.9101H6.061V12.6212Z" /></svg>`,
      title: { bn: 'রশিদ পরিচালনা', en: 'Receipt Management' }
    },
    {
      icon: `<svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path fill-rule="evenodd" clip-rule="evenodd" d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z"/></svg>`,
      title: { bn: 'আর্থিক সারসংক্ষেপ', en: 'Financial Summary' }
    },
    {
      icon: `<svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.8c-2.95 3.3-5.2 5.98-5.2 9.03A5.2 5.2 0 0 0 12 17.03a5.2 5.2 0 0 0 5.2-5.2c0-3.05-2.25-5.73-5.2-9.03Zm1.05 8.36h-1.47l.58-2.07c.05-.18-.15-.31-.29-.18l-2.38 2.27h1.47l-.58 2.07c-.05.18.15.31.29.18l2.38-2.27ZM8.9 20.5h6.2V19h-6.2v1.5Z"/></svg>`,
      title: { bn: 'ইউটিলিটি বিল পরিচালনা', en: 'Utility Bill Management' }
    },
    {
      icon: `<svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M21.1 7.2l-4.3-4.3a1.2 1.2 0 0 0-1.7 0l-1.6 1.6l1.8 1.8l-1.4 1.4l-3-3a1.2 1.2 0 0 0-1.7 0L8 5.8c-.47.47-.47 1.24 0 1.7l1.5 1.5l-5.8 5.8a2.2 2.2 0 0 0 0 3.1l1.4 1.4a2.2 2.2 0 0 0 3.1 0l5.8-5.8l1.5 1.5c.47.47 1.24.47 1.7 0l1.1-1.1a1.2 1.2 0 0 0 0-1.7l-3-3l1.4-1.4l1.8 1.8l1.6-1.6c.47-.47.47-1.24 0-1.7Z"/></svg>`,
      title: { bn: 'রক্ষণাবেক্ষণ খরচ ট্র্যাকিং', en: 'Maintenance Cost Tracking' }
    },
    {
      icon: `<svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path fill-rule="evenodd" clip-rule="evenodd" d="M18.9 6.5H5.1l-1.3 4h17.4l-2.3-4zM4.1 12v6c0 1.1.9 2 2 2h11.8c1.1 0 2-.9 2-2v-6H4.1zm5.9 6H7.9v-4h2.1v4zm6.1 0h-2.1v-4h2.1v4z"/></svg>`,
      title: { bn: 'গ্যারেজ পরিচালনা', en: 'Garage Management' }
    },
    {
      icon: `<svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path fill-rule="evenodd" clip-rule="evenodd" d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-5h2v5zm4 0h-2v-3h2v3zm4 0h-2v-5h2v5z"/></svg>`,
      title: { bn: 'রিপোর্টিং সিস্টেম', en: 'Reporting System' }
    },
    {
      icon: `<svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path fill-rule="evenodd" clip-rule="evenodd" d="M12 12c2.2 0 4-1.8 4-4s-1.8-4-4-4-4 1.8-4 4 1.8 4 4 4zm0 2c-2.7 0-8 1.3-8 4v2h16v-2c0-2.7-5.3-4-8-4z"/></svg>`,
      title: { bn: 'কর্মচারী পরিচালনা (শীঘ্রই)', en: 'Employee Management (Coming Soon)' }
    },
    {
      icon: `<svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path fill-rule="evenodd" clip-rule="evenodd" d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7v-5z"/></svg>`,
      title: { bn: 'বিল নোটিশ সিস্টেম (শীঘ্রই)', en: 'Bill Notice System (Coming Soon)' }
    }
  ];

  readonly rolesHeading: LocalizedCopy = {
    bn: 'ইউজার লগইন প্যানেলে:',
    en: 'User login panel:'
  };

  readonly roles: Array<{ icon: IconMarkup; text: LocalizedCopy }> = [
    {
      icon: `<svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3l6 2.7v5.4c0 4-2.5 7.7-6 8.9c-3.5-1.2-6-4.9-6-8.9V5.7L12 3Z"/><path d="M12 9.2a1.9 1.9 0 1 0 0-3.8a1.9 1.9 0 0 0 0 3.8Z"/><path d="M9.3 14.5c.5-1.4 1.5-2.1 2.7-2.1s2.2.7 2.7 2.1"/></svg>`,
      text: { bn: 'অ্যাডমিন (এপার্টমেন্ট মালিক)', en: 'Admin (flat owner)' }
    },
    {
      icon: `<svg width="21" height="21" viewBox="0 0 24 24" fill="currentColor"><path d="M4 7a3 3 0 1 1 6 0a3 3 0 0 1-6 0z"/><path d="M14 7a3 3 0 1 1 6 0a3 3 0 0 1-6 0z"/><path d="M4 17a3 3 0 1 1 6 0a3 3 0 0 1-6 0z"/><path d="M15 16h4a1 1 0 1 1 0 2h-4a1 1 0 1 1 0-2z"/></svg>`,
      text: { bn: 'সাবইউজার (পরিচালনাকারী)', en: 'Subuser (manager)' }
    },
    {
      icon: `<svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path d="M10 11a3.5 3.5 0 1 0 0-7a3.5 3.5 0 0 0 0 7Z"/><path d="M4 19c.8-2.3 3-3.8 6-3.8s5.2 1.5 6 3.8"/><path d="M17 8h3"/><path d="M18.5 6.5v3"/><circle cx="18.5" cy="8" r="2.5"/></svg>`,
      text: { bn: 'ভাড়াটিয়া', en: 'Renter' }
    }
  ];

  readonly footerNote: LocalizedCopy = {
    bn: 'ফ্ল্যাট ম্যানেজমেন্ট সফটওয়্যারটি আপনাকে এক নজরে আপনার হাতে পুরো ফ্ল্যাটের নিয়ন্ত্রণ করতে সাহায্য করবে।',
    en: 'The software helps you control your flat operations from one dashboard.'
  };

  readonly termsLabelPrefix: LocalizedCopy = {
    bn: 'আমি ',
    en: 'I agree to the '
  };

  readonly termsLabelLink: LocalizedCopy = {
    bn: 'শর্তাবলী',
    en: 'Terms & Conditions'
  };

  readonly termsLabelSuffix: LocalizedCopy = {
    bn: ' মেনে নিচ্ছি',
    en: ''
  };

  readonly termsModalTitle: LocalizedCopy = {
    bn: 'সেবার শর্তাবলী',
    en: 'Terms & Conditions'
  };

  readonly termsModalIntro: LocalizedCopy = {
    bn: 'এই সফটওয়্যার ব্যবহার করে আপনি নিচের শর্তাবলী মেনে নিচ্ছেন। এই শর্তাবলী আপনার ফ্ল্যাট, ভাড়াটিয়া, সাবইউজার এবং সংশ্লিষ্ট ব্যক্তিগত তথ্য ব্যবহারের দায়িত্ব পরিষ্কার করে।',
    en: 'By using this software, you agree to the following terms. These terms define your responsibilities for managing flats, renters, subusers, and related personal data in the system.'
  };

  readonly termsSections: TermsSection[] = [
    {
      title: {
        bn: '১. অনুমোদিত ব্যবহার',
        en: '1. Permitted Use'
      },
      items: {
        bn: [
          'এই সফটওয়্যার শুধুমাত্র বৈধ ফ্ল্যাট, বাড়ি, ভাড়া, বিল, রক্ষণাবেক্ষণ এবং সংশ্লিষ্ট প্রশাসনিক কাজ পরিচালনার জন্য ব্যবহার করতে হবে',
          'প্রতারক, বিভ্রান্তিকর, হয়রানিমূলক, বেআইনি বা অননুমোদিত কাজে এই সিস্টেম ব্যবহার করা যাবে না'
        ],
        en: [
          'The software may be used only for lawful flat, housing, rent, billing, maintenance, and related administration activities',
          'The system must not be used for fraudulent, misleading, abusive, unlawful, or unauthorized purposes'
        ]
      }
    },
    {
      title: {
        bn: '২. ব্যক্তিগত তথ্যের দায়িত্ব',
        en: '2. Responsibility for Personal Data'
      },
      items: {
        bn: [
          'ভাড়াটিয়া, মালিক, সাবইউজার বা অন্য কারও ইমেইল, মোবাইল নম্বর, জাতীয় পরিচয়পত্র নম্বর, জন্ম নিবন্ধন নম্বর, ছবি বা নথি যোগ করার আগে প্রয়োজনীয় অধিকার, সম্মতি বা আইনগত ভিত্তি নিশ্চিত করা আপনার দায়িত্ব',
          'আপনি যে তথ্য যোগ করবেন তার সঠিকতা, আপডেট অবস্থা এবং বৈধতা আপনার দায়িত্ব'
        ],
        en: [
          'Before adding any renter, owner, subuser, or other person’s email, mobile number, national ID number, birth registration number, image, or document, you are responsible for having the necessary rights, consent, or lawful basis',
          'You are responsible for the accuracy, currency, and legality of the information you enter'
        ]
      }
    },
    {
      title: {
        bn: '৩. ন্যূনতম তথ্য সংগ্রহ ও নিরাপত্তা',
        en: '3. Data Minimization and Security'
      },
      items: {
        bn: [
          'যে কাজের জন্য যতটুকু তথ্য প্রয়োজন, কেবল ততটুকু তথ্য সংরক্ষণ করার পরামর্শ দেওয়া হয়',
          'আপনার লগইন, পাসওয়ার্ড, ডিভাইস অ্যাক্সেস এবং অভ্যন্তরীণ ব্যবহারকারী অনুমতি সুরক্ষিত রাখা আপনার দায়িত্ব'
        ],
        en: [
          'You should collect and store only the information reasonably necessary for your property management work',
          'You are responsible for securing your login, password, device access, and internal user permissions'
        ]
      }
    },
    {
      title: {
        bn: '৪. সংবেদনশীল নথি ও শেয়ারিং',
        en: '4. Sensitive Documents and Sharing'
      },
      items: {
        bn: [
          'সংবেদনশীল নথি আপলোড, ডাউনলোড, শেয়ার, প্রিন্ট বা রপ্তানি করার সিদ্ধান্ত এবং তার ঝুঁকি আপনার প্রতিষ্ঠানের দায়িত্ব',
          'যাদের তথ্য আপনি সিস্টেমে রাখবেন, তাদের তথ্য তৃতীয় পক্ষের সাথে শেয়ার করলে প্রয়োজনীয় অনুমতি ও আইনগত ভিত্তি নিশ্চিত করতে হবে'
        ],
        en: [
          'Your organization is responsible for the risks of uploading, downloading, sharing, printing, or exporting sensitive documents',
          'If you share stored information with third parties, you must ensure the required permission and lawful basis for that disclosure'
        ]
      }
    },
    {
      title: {
        bn: '৫. সংরক্ষণ, মুছে ফেলা ও সমাপ্তি',
        en: '5. Retention, Deletion, and Termination'
      },
      items: {
        bn: [
          'আপনার প্রয়োজন শেষ হলে বা আইনগতভাবে আর প্রয়োজন না থাকলে তথ্য মুছে ফেলা বা সীমিত করার দায়িত্ব আপনার',
          'অপব্যবহার, নিরাপত্তা ঝুঁকি, বেআইনি কার্যক্রম বা এই শর্তাবলী ভঙ্গের ক্ষেত্রে অ্যাকাউন্ট সীমিত, স্থগিত বা বন্ধ করা হতে পারে'
        ],
        en: [
          'You are responsible for deleting or restricting data when it is no longer needed or no longer lawful to retain',
          'Accounts may be limited, suspended, or terminated for misuse, security risk, unlawful activity, or breach of these terms'
        ]
      }
    },
    {
      title: {
        bn: '৬. দায়সীমা ও ব্যবহারকারীর সম্মতি',
        en: '6. Liability and User Acknowledgement'
      },
      items: {
        bn: [
          'সফটওয়্যারটি ব্যবহারের সময় তথ্য এন্ট্রি, ব্যবহারকারীর সিদ্ধান্ত, আইনগত অনুমতি, নোটিশ, সম্মতি সংগ্রহ এবং স্থানীয় আইন মেনে চলার প্রধান দায়িত্ব ব্যবহারকারীর',
          'প্রযোজ্য আইনে যতটুকু অনুমোদিত, সফটওয়্যার সেবাদাতা পরোক্ষ ক্ষতি, ব্যবহারকারীর ভুল তথ্য, অননুমোদিত ব্যবহার বা ব্যবহারকারীর আইন লঙ্ঘনের জন্য দায়ী থাকবে না'
        ],
        en: [
          'You remain primarily responsible for data entry, user decisions, legal permissions, required notices, consent collection, and compliance with applicable local law',
          'To the extent permitted by law, the software provider is not responsible for indirect loss, user-entered inaccuracies, unauthorized use, or legal violations caused by the user or the user’s organization'
        ]
      }
    }
  ];

  readonly termsModalClosing: LocalizedCopy = {
    bn: 'এই শর্তাবলী ঝুঁকি কমাতে সহায়তা করে, তবে চূড়ান্ত আইনগত সুরক্ষার জন্য স্থানীয় আইনজীবীর পর্যালোচনা প্রয়োজন হতে পারে',
    en: 'These terms help reduce operational risk, but final legal protection may still require review by a local lawyer'
  };

  stepOneForm: FormGroup;
  stepTwoForm: FormGroup;
  stepThreeForm: FormGroup;

  strengthBars = [
    { filled: false },
    { filled: false },
    { filled: false },
    { filled: false }
  ];

  constructor(
    private readonly fb: FormBuilder,
    private readonly router: Router,
    private readonly auth: AuthService,
    private readonly attachments: AttachmentService,
    private readonly language: LanguageService,
    private readonly sanitizer: DomSanitizer
  ) {
    this.features.forEach((feature) => {
      feature.icon = this.sanitizer.bypassSecurityTrustHtml(`${feature.icon}`);
    });
    this.roles.forEach((role) => {
      role.icon = this.sanitizer.bypassSecurityTrustHtml(`${role.icon}`);
    });
    this.updateFeatureVisibility();
    this.stepOneForm = this.fb.group({
      fullName: ['', Validators.required],
      organizationName: ['', Validators.required],
      mobileNumber: ['', [Validators.pattern(/^1\d{9}$/)]]
    });
    this.stepTwoForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required, Validators.minLength(6)]],
      confirmPassword: ['', Validators.required],
      acceptTerms: [false, Validators.requiredTrue]
    });
    this.stepThreeForm = this.fb.group({
      otp: ['', [Validators.required, Validators.pattern(/^[0-9]{4,8}$/)]]
    });

    this.stepTwoForm.get('password')?.valueChanges.subscribe((value: string) => this.updateStrengthBars(value));
    this.stepTwoForm.get('email')?.valueChanges.subscribe(() => {
      this.clearEmailAlreadyInUseError();
      this.error = null;
      this.status = null;
      this.isOtpStepUnlocked = false;
      if (this.currentStep === 3) {
        this.currentStep = 2;
      }
    });
  }

  ngAfterViewInit(): void {
    this.scheduleFeatureVisibilityUpdate();
    this.scheduleDeferredFeatureVisibilityUpdates();

    if (typeof ResizeObserver === 'undefined' || !this.featuresCardRef?.nativeElement) {
      return;
    }

    this.featuresResizeObserver = new ResizeObserver(() => this.scheduleFeatureVisibilityUpdate());
    this.featuresResizeObserver.observe(this.featuresCardRef.nativeElement);
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.scheduleFeatureVisibilityUpdate();
  }

  @HostListener('document:keydown.escape')
  onEscapePressed(): void {
    if (this.isTermsModalOpen) {
      this.closeTermsModal();
    }
  }

  updateStrengthBars(password: string): void {
    const len = `${password ?? ''}`.length;
    this.strengthBars[0].filled = len >= 6;
    this.strengthBars[1].filled = len >= 8;
    this.strengthBars[2].filled = len >= 10;
    this.strengthBars[3].filled = len >= 12;
  }

  continueToCredentials(): void {
    this.normalizeMobileInput();
    if (this.stepOneForm.invalid) {
      this.stepOneForm.markAllAsTouched();
      return;
    }
    this.isOtpStepUnlocked = false;
    this.currentStep = 2;
  }

  requestOtp(): void {
    this.clearEmailAlreadyInUseError();
    this.isOtpStepUnlocked = false;
    this.currentStep = 2;
    if (this.stepTwoForm.invalid) {
      this.stepTwoForm.markAllAsTouched();
      return;
    }

    const password = `${this.stepTwoForm.value?.password ?? ''}`;
    const confirmPassword = `${this.stepTwoForm.value?.confirmPassword ?? ''}`;
    if (password !== confirmPassword) {
      this.error = this.t('signUp.passwordMismatch', 'Password and retype password must match.');
      return;
    }

    this.error = null;
    this.status = null;
    this.isSendingOtp = true;
    const email = `${this.stepTwoForm.value?.email ?? ''}`.trim();

    this.auth.requestRegistrationOtp({ email })
      .pipe(finalize(() => (this.isSendingOtp = false)))
      .subscribe({
        next: (res: any) => {
          const responseCode = `${res?.code ?? ''}`;
          if (responseCode && responseCode !== '000') {
            const message = `${res?.message ?? 'Failed to send OTP.'}`;
            if (message.toLowerCase().includes('email already')) {
              this.markEmailAlreadyInUse(message);
            } else {
              this.error = message;
            }
            return;
          }
          this.status = this.t(
            'signUp.status.otpSentDetailed',
            'OTP sending successful to your mail. If not found then please check <strong>spam folder</strong>.'
          );
          this.startOtpCountdown();
          this.isOtpStepUnlocked = true;
          this.currentStep = 3;
        },
        error: (err: any) => {
          const message = `${err?.error?.message ?? err?.message ?? 'Failed to send OTP.'}`;
          if (message.toLowerCase().includes('email already')) {
            this.markEmailAlreadyInUse(message);
            return;
          }
          this.error = message;
        }
      });
  }

  completeSignup(): void {
    if (this.stepThreeForm.invalid) {
      this.stepThreeForm.markAllAsTouched();
      return;
    }

    this.error = null;
    this.status = null;
    this.isRegistering = true;
    this.otpValue = `${this.stepThreeForm.value?.otp ?? ''}`.trim();

    this.auth.register({
      name: `${this.stepOneForm.value?.fullName ?? ''}`.trim(),
      organizationName: `${this.stepOneForm.value?.organizationName ?? ''}`.trim(),
      mobileNumber: combineBangladeshMobileNumber(this.stepOneForm.value?.mobileNumber),
      email: `${this.stepTwoForm.value?.email ?? ''}`.trim(),
      password: `${this.stepTwoForm.value?.password ?? ''}`,
      otp: this.otpValue
    })
      .pipe(finalize(() => (this.isRegistering = false)))
      .subscribe({
        next: (res: any) => {
          if (res?.code && `${res.code}` !== '000') {
            this.error = `${res?.message ?? 'Registration failed.'}`;
            return;
          }
          this.registeredUserId = Number(res?.refId ?? 0) || null;
          this.status = this.t('signUp.signupCompleted', 'Signup completed.');
          this.error = null;
          this.isImageModalOpen = true;
        },
        error: (err: any) => {
          this.error = `${err?.error?.message ?? err?.message ?? 'Registration failed.'}`;
        }
      });
  }

  onImageSelected(event: Event): void {
    const input = event.target as HTMLInputElement | null;
    const file = (input?.files ?? [])[0];
    if (!file) {
      if (input) {
        input.value = '';
      }
      return;
    }

    const allowedExtensions = ATTACHMENT_ALLOWED_EXTENSIONS_BY_FEATURE[FeatureTypeEnum.ORGANIZATION_LOGO_OR_EMPLOYEE_IMAGE];
    const normalizedName = `${file.name ?? ''}`.toLowerCase();
    if (!allowedExtensions.some((ext) => normalizedName.endsWith(ext))) {
      this.error = `Only ${allowedExtensions.join(', ')} files are allowed.`;
      if (input) {
        input.value = '';
      }
      return;
    }

    this.error = null;
    this.imageCropSourceFile = file;

    if (input) {
      input.value = '';
    }
  }

  onOrganizationImageCropCancelled(): void {
    this.imageCropSourceFile = null;
  }

  onOrganizationImageCropped(file: File): void {
    this.imageCropSourceFile = null;
    this.clearSelectedImagePreview();
    this.selectedImageFile = file;
    this.selectedImageName = file.name;
    this.selectedImagePreviewUrl = URL.createObjectURL(file);
    this.error = null;
  }

  skipImageStep(): void {
    this.finalizeSignup(false);
  }

  finishWithImage(): void {
    this.finalizeSignup(!!this.selectedImageFile);
  }

  get organizationLogoFallbackLabel(): string {
    const organizationName = `${this.stepOneForm.value?.organizationName ?? ''}`.trim();
    if (!organizationName) {
      return 'O';
    }
    return organizationName.charAt(0).toUpperCase();
  }

  get otpExpireLabel(): string {
    if (!this.otpExpiresInSeconds) {
      return '';
    }
    const min = Math.floor(this.otpExpiresInSeconds / 60);
    const sec = this.otpExpiresInSeconds % 60;
    return `${String(min).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
  }

  goToStep(step: 1 | 2 | 3): void {
    if (step === 1) {
      this.currentStep = 1;
      return;
    }
    if (step === 2 && this.currentStep >= 2) {
      this.currentStep = 2;
      return;
    }
    if (step === 3 && this.isOtpStepUnlocked) {
      this.currentStep = 3;
    }
  }

  hasError(form: FormGroup, controlName: string): boolean {
    const control = form.get(controlName);
    return !!(control && control.invalid && (control.dirty || control.touched));
  }

  shouldShowFieldError(form: FormGroup, controlName: string, errorKey?: string): boolean {
    if (this.focusedField === controlName) {
      return false;
    }
    const control = form.get(controlName);
    if (!control || !(control.touched || control.dirty)) {
      return false;
    }
    if (!errorKey) {
      return control.invalid;
    }
    return control.hasError(errorKey);
  }

  isOtpButtonDisabled(): boolean {
    const emailControl = this.stepTwoForm.get('email');
    return this.isSendingOtp || !!emailControl?.errors?.['emailAlreadyInUse'];
  }

  onFieldFocus(controlName: string): void {
    this.focusedField = controlName;
  }

  onFieldBlur(form: FormGroup, controlName: string): void {
    this.focusedField = this.focusedField === controlName ? null : this.focusedField;
    form.get(controlName)?.markAsTouched();
  }

  openTermsModal(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.isTermsModalOpen = true;
  }

  closeTermsModal(): void {
    this.isTermsModalOpen = false;
  }

  localize(copy: LocalizedCopy): string {
    return this.language.currentLanguage === 'bn' ? copy.bn : copy.en;
  }

  localizeList(copy: LocalizedListCopy): string[] {
    return this.language.currentLanguage === 'bn' ? copy.bn : copy.en;
  }

  get isBangla(): boolean {
    return this.language.currentLanguage === 'bn';
  }

  get visibleFeatures() {
    return this.renderExpandedFeatures ? this.features : this.features.slice(0, this.collapsedFeatureCount);
  }

  get shouldShowFeatureToggle(): boolean {
    return this.features.length > this.collapsedFeatureCount;
  }

  normalizeMobileInput(): void {
    const control = this.stepOneForm.get('mobileNumber');
    control?.setValue(normalizeBangladeshMobileLocalInput(control.value), { emitEvent: false });
  }

  stepOnePasswordMismatch(): boolean {
    const password = `${this.stepTwoForm.value?.password ?? ''}`;
    const confirmPassword = `${this.stepTwoForm.value?.confirmPassword ?? ''}`;
    const confirmControl = this.stepTwoForm.get('confirmPassword');
    return (
      this.focusedField !== 'confirmPassword' &&
      !!confirmControl &&
      (confirmControl.dirty || confirmControl.touched) &&
      !!password &&
      !!confirmPassword &&
      password !== confirmPassword
    );
  }

  private startOtpCountdown(): void {
    this.otpCountdownSub?.unsubscribe();
    this.otpExpiresInSeconds = APP_OTP_EXPIRY_SECONDS;
    this.otpCountdownSub = interval(1000).subscribe(() => {
      if (this.otpExpiresInSeconds <= 1) {
        this.otpExpiresInSeconds = 0;
        this.otpCountdownSub?.unsubscribe();
        return;
      }
      this.otpExpiresInSeconds -= 1;
    });
  }

  ngOnDestroy(): void {
    this.otpCountdownSub?.unsubscribe();
    this.clearSelectedImageSelection();
    this.featuresResizeObserver?.disconnect();
    if (this.featureVisibilityFrame !== null && typeof window !== 'undefined') {
      window.cancelAnimationFrame(this.featureVisibilityFrame);
    }
    if (this.featureGridTransitionTimeout !== null) {
      clearTimeout(this.featureGridTransitionTimeout);
    }
    this.deferredFeatureVisibilityTimeouts.forEach(timeoutId => clearTimeout(timeoutId));
  }

  private scheduleFeatureVisibilityUpdate(animate = false): void {
    if (typeof window === 'undefined') {
      this.updateFeatureVisibility(animate);
      return;
    }

    if (this.featureVisibilityFrame !== null) {
      window.cancelAnimationFrame(this.featureVisibilityFrame);
    }

    this.featureVisibilityFrame = window.requestAnimationFrame(() => {
      this.featureVisibilityFrame = null;
      this.updateFeatureVisibility(animate);
    });
  }

  private scheduleDeferredFeatureVisibilityUpdates(): void {
    if (typeof window === 'undefined') {
      return;
    }

    this.deferredFeatureVisibilityTimeouts.forEach(timeoutId => clearTimeout(timeoutId));
    this.deferredFeatureVisibilityTimeouts = [
      setTimeout(() => this.scheduleFeatureVisibilityUpdate(), 0),
      setTimeout(() => this.scheduleFeatureVisibilityUpdate(), 180),
      setTimeout(() => this.scheduleFeatureVisibilityUpdate(), 420)
    ];
  }

  private updateFeatureVisibility(animate = false): void {
    if (typeof window === 'undefined') {
      this.collapsedFeatureCount = Math.min(this.features.length, 6);
      return;
    }

    const featureGrid = this.featureGridRef?.nativeElement;
    const featuresCard = this.featuresCardRef?.nativeElement;
    const featuresHeading = this.featuresHeadingRef?.nativeElement;

    const isMobileWidth = window.innerWidth <= 640;
    let columns = isMobileWidth ? 1 : window.innerWidth <= 1180 ? 2 : 3;
    let rows = window.innerHeight <= 760 ? 2 : window.innerHeight <= 980 ? 3 : 4;
    const minimumRows = isMobileWidth ? 3 : 1;

    if (featureGrid && featuresCard && featuresHeading) {
      const gridStyle = window.getComputedStyle(featureGrid);
      const cardStyle = window.getComputedStyle(featuresCard);
      const headingStyle = window.getComputedStyle(featuresHeading);
      const sampleFeature = featureGrid.querySelector<HTMLElement>('.auth-showcase__feature');
      const sampleButton = featureGrid.querySelector<HTMLElement>('.auth-showcase__more-btn--tile');
      const trackList = gridStyle.gridTemplateColumns.split(' ').filter(Boolean);
      const measuredColumns = trackList.length;
      const rowGap = Number.parseFloat(gridStyle.rowGap || '0') || 0;
      const cardPaddingTop = Number.parseFloat(cardStyle.paddingTop || '0') || 0;
      const cardPaddingBottom = Number.parseFloat(cardStyle.paddingBottom || '0') || 0;
      const headingMarginBottom = Number.parseFloat(headingStyle.marginBottom || '0') || 0;
      const tileHeight = Math.max(
        sampleFeature?.getBoundingClientRect().height ?? 0,
        sampleButton?.getBoundingClientRect().height ?? 0,
        window.innerWidth <= 640 ? 76 : 72
      );
      const availableGridHeight = featuresCard.clientHeight - cardPaddingTop - cardPaddingBottom - featuresHeading.clientHeight - headingMarginBottom;

      if (measuredColumns > 0) {
        columns = measuredColumns;
      }

      if (availableGridHeight > 0 && tileHeight > 0) {
        rows = Math.max(minimumRows, Math.floor((availableGridHeight + rowGap) / (tileHeight + rowGap)));
      }
    }

    rows = Math.max(rows, minimumRows);

    this.featureGridColumns = Math.max(columns, 1);
    this.featureGridRowGap = Math.max(this.featureGridRowGap, 0);
    this.featureGridTileHeight = Math.max(this.featureGridTileHeight, 1);

    const totalSlots = Math.max(columns * rows, columns === 1 ? 2 : columns);
    const needsToggle = this.features.length > totalSlots;

    this.collapsedFeatureCount = Math.min(this.features.length, needsToggle ? Math.max(totalSlots - 1, 1) : totalSlots);
    if (!this.isFeatureGridTransitioning) {
      this.applyFeatureGridHeight(animate);
    }
  }

  private applyFeatureGridHeight(animate: boolean): void {
    if (typeof window === 'undefined') {
      return;
    }

    const grid = this.featureGridRef?.nativeElement;
    if (!grid) {
      return;
    }

    if (this.featureGridTransitionTimeout !== null) {
      clearTimeout(this.featureGridTransitionTimeout);
      this.featureGridTransitionTimeout = null;
    }

    const setTargetHeight = () => {
      const targetHeight = Math.ceil(grid.scrollHeight || grid.getBoundingClientRect().height || 0);
      this.featureGridMaxHeight = targetHeight > 0 ? targetHeight : null;
    };

    if (!animate) {
      setTargetHeight();
      return;
    }

    window.requestAnimationFrame(() => {
      setTargetHeight();
      this.featureGridTransitionTimeout = setTimeout(() => {
        this.featureGridTransitionTimeout = null;
        setTargetHeight();
      }, this.featureGridTransitionMs);
    });
  }

  private getCollapsedGridHeight(): number | null {
    const columns = Math.max(this.featureGridColumns, 1);
    const itemsInCollapsedView = this.collapsedFeatureCount + (this.features.length > this.collapsedFeatureCount ? 1 : 0);
    const rows = Math.max(1, Math.ceil(itemsInCollapsedView / columns));
    const collapsedHeight = rows * this.featureGridTileHeight + Math.max(0, rows - 1) * this.featureGridRowGap;
    return collapsedHeight > 0 ? Math.ceil(collapsedHeight) : null;
  }

  private finalizeSignup(uploadLogo: boolean): void {
    const email = `${this.stepTwoForm.value?.email ?? ''}`.trim();
    const password = `${this.stepTwoForm.value?.password ?? ''}`;
    if (!email || !password || this.isFinalizingSignup) {
      return;
    }

    this.isFinalizingSignup = true;
    this.error = null;

    this.auth.login({ email, password }).pipe(
      switchMap(() => {
        if (!uploadLogo || !this.selectedImageFile || !Number.isFinite(this.registeredUserId as number)) {
          return of(null);
        }
        return this.attachments.manageAttachment({
          featureType: FeatureTypeEnum.ORGANIZATION_LOGO_OR_EMPLOYEE_IMAGE,
          featureId: Number(this.registeredUserId),
          actionType: 1,
          files: [this.selectedImageFile],
          service: 'ORGANIZATION_LOGO_OR_EMPLOYEE_IMAGE'
        });
      }),
      finalize(() => {
        this.isFinalizingSignup = false;
      })
    ).subscribe({
      next: () => {
        this.isImageModalOpen = false;
        this.clearSelectedImageSelection();
        this.router.navigate(['/dashboard']);
      },
      error: (err: any) => {
        this.error = `${err?.error?.message ?? err?.message ?? 'Failed to complete signup.'}`;
      }
    });
  }

  private clearSelectedImageSelection(): void {
    this.clearSelectedImagePreview();
    this.selectedImageFile = null;
    this.selectedImageName = '';
    this.imageCropSourceFile = null;
  }

  private clearSelectedImagePreview(): void {
    if (this.selectedImagePreviewUrl) {
      URL.revokeObjectURL(this.selectedImagePreviewUrl);
      this.selectedImagePreviewUrl = null;
    }
  }

  private markEmailAlreadyInUse(message: string): void {
    const emailControl = this.stepTwoForm.get('email');
    if (!emailControl) {
      this.error = message;
      return;
    }
    emailControl.setErrors({ ...(emailControl.errors ?? {}), emailAlreadyInUse: true });
    emailControl.markAsTouched();
    this.error = message;
    this.status = null;
  }

  private clearEmailAlreadyInUseError(): void {
    const emailControl = this.stepTwoForm.get('email');
    if (!emailControl?.errors?.['emailAlreadyInUse']) {
      return;
    }
    const { emailAlreadyInUse, ...remainingErrors } = emailControl.errors;
    emailControl.setErrors(Object.keys(remainingErrors).length ? remainingErrors : null);
  }

  private t(key: string, fallback: string): string {
    return this.language.translate(key, fallback);
  }
}
